import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

import { API } from '../config/api';
import { trackLogin, trackLogout } from '../lib/analytics';
import { clearApiCache } from '../lib/swRegister';

// Axios interceptor: attach token from localStorage as Bearer header on
// EVERY platform (PWA web, PWA installed, native) and on EVERY request
// inject the current UI language so the backend can keep
// `users.language` in sync.
//
// Why always-on bearer: on iOS PWA installed-on-home, Safari/WKWebView
// often holds onto a stale `session_token` cookie from a previous
// account that the user already logged out from. Sending the bearer
// (which is freshly written to localStorage on every successful login)
// gives the backend an unambiguous, current identity. The backend
// `get_current_user` resolver prefers Authorization Bearer over the
// cookie precisely for this case.
axios.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  const lang = localStorage.getItem('app_language');
  if (lang) {
    config.headers['X-App-Language'] = lang;
  }
  // Platform header — Capacitor.getPlatform() returns 'ios'/'android'/'web'.
  // Backend uses this for analytics (Admin → Statistics → Audience → Per piattaforma).
  try {
    const plat = window?.Capacitor?.getPlatform?.() || 'web';
    config.headers['X-Slimmy-Platform'] = plat;
  } catch { /* default to nothing */ }
  return config;
});

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const bootToken = localStorage.getItem('session_token');
    console.log('[AuthBootstrap] token present', !!bootToken);
    // Check for auth=success in URL (from Google OAuth callback)
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('auth') === 'success') {
      // Remove the query param from URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    // Legacy: If returning from Emergent OAuth callback, handle it
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // Native-only: if the user previously enabled biometric login,
    // gate the cold start with a Face ID / Touch ID prompt before
    // we let the (already-cookie-authed) session render any UI.
    // On failure we hit /auth/logout so the cookie is invalidated and
    // the user lands on the normal login screen.
    try {
      const { gateAppStartWithBiometric } = await import('../lib/biometric');
      const gate = await gateAppStartWithBiometric();
      if (gate === 'denied') {
        try {
          await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
        } catch (_) { /* ignore */ }
        localStorage.removeItem('session_token');
        setUser(null);
        setLoading(false);
        return;
      }
    } catch (_) { /* swallow — biometry is optional */ }
    
    try {
      console.log('[AuthBootstrap] loading user');
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      console.log('[AuthBootstrap] user loaded', !!response.data);

      // Lifetime Premium promo: if the user just received the grant
      // during registration, the backend sets
      // `lifetime_promo_pending_celebration: true`. Show a celebratory
      // toast once and clear the flag server-side so it doesn't repeat.
      if (response.data?.lifetime_promo_pending_celebration) {
        try {
          const lang = localStorage.getItem('app_language') || 'it';
          const messages = {
            it: '🎉 Hai ricevuto il Premium a vita gratuito!',
            en: '🎉 You received free Lifetime Premium!',
            es: '🎉 ¡Recibiste Premium de por vida gratis!',
            pt: '🎉 Você ganhou o Premium vitalício grátis!',
            de: '🎉 Du hast lebenslanges Premium gratis erhalten!',
            fr: '🎉 Vous avez reçu le Premium à vie gratuit !',
          };
          const sub = {
            it: 'Goditi tutte le funzionalità Premium senza limiti, per sempre.',
            en: 'Enjoy every Premium feature, forever.',
            es: 'Disfruta de todas las funciones Premium, para siempre.',
            pt: 'Aproveite todos os recursos Premium, para sempre.',
            de: 'Genieße alle Premium-Funktionen für immer.',
            fr: 'Profitez de toutes les fonctionnalités Premium, pour toujours.',
          };
          toast.success(messages[lang] || messages.en, {
            description: sub[lang] || sub.en,
            duration: 8000,
          });
          axios.post(`${API}/auth/lifetime-promo/celebration-seen`, {}, { withCredentials: true }).catch(() => {});
        } catch (_) {}
      }
      
      // Sync language: backend is source of truth when explicitly set,
      // BUT a fresher local choice (made offline / on this device) wins and
      // gets pushed back. We dispatch the event first WITHOUT touching
      // localStorage so LanguageContext can compare local vs backend and
      // decide which one to keep.
      try {
        const langResp = await axios.get(`${API}/user/language`, { withCredentials: true });
        const backendLang = langResp.data?.language;
        const isSet = langResp.data?.is_set;

        if (isSet && backendLang) {
          // Let LanguageContext.handleBackendSync resolve local-vs-backend.
          window.dispatchEvent(new CustomEvent('backend-language-sync', { detail: { language: backendLang } }));
        } else {
          // No language set in backend - push frontend language to backend
          const frontendLang = localStorage.getItem('app_language');
          if (frontendLang) {
            axios.put(`${API}/user/language`, { language: frontendLang }, { withCredentials: true }).catch(() => {});
          }
        }
      } catch (_) {}
    } catch (e) {
      console.error('[AuthBootstrap] failed', e?.response?.status || e?.message);
      setUser(null);
    } finally {
      setLoading(false);
      console.log('[AuthBootstrap] completed');
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Post-Google deep link (native): handleDeepLink saves the token and
  // dispatches this event — load the user WITHOUT any page reload.
  useEffect(() => {
    const onAuthSuccess = async () => {
      console.log('[AuthBootstrap] slimmy-auth-success received');
      setLoading(true);
      try {
        console.log('[AuthBootstrap] loading user');
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
        console.log('[AuthBootstrap] user loaded', !!response.data);
      } catch (e) {
        console.error('[AuthBootstrap] failed', e?.response?.status || e?.message);
        setUser(null);
      } finally {
        setLoading(false);
        console.log('[AuthBootstrap] completed');
      }
    };
    window.addEventListener('slimmy-auth-success', onAuthSuccess);
    return () => window.removeEventListener('slimmy-auth-success', onAuthSuccess);
  }, []);

  const login = async () => {
    try {
      // Get Google OAuth URL from backend
      const response = await axios.get(`${API}/auth/google/login`);
      // Redirect to Google
      window.location.href = response.data.auth_url;
    } catch (error) {
      console.error('Login error:', error);
      // Fallback to Emergent Auth if Google OAuth fails
      const redirectUrl = window.location.origin + '/dashboard';
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      trackLogout();
    } catch (e) {
      console.error('Logout error:', e);
    }
    // Clear session token from localStorage
    localStorage.removeItem('session_token');
    // Also wipe the biometric secure-storage copy — otherwise the next
    // app start would silently re-login the user who just logged out.
    try {
      const { disableBiometricLogin } = await import('../lib/biometric');
      await disableBiometricLogin();
    } catch (_) { /* ignore */ }
    // Drop cached user-scoped API responses held by the service worker
    clearApiCache();
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch {
      // ignore
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, authLoading: loading, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};
